import { useCallback, useEffect, useRef, useState } from 'react';

import { CBCC_EVENT, trackCbccEvent } from '../services/analytics';
import { showCbccErrorToast } from '../services/cbcc-toast';
import { fetchManageCard } from '../services/fetch-manage-card';
import { handleManageCardRedirection } from '../services/redirect-to-bank';
import type { BankRedirectionResponse } from '../types';
import { useResetOnReturn } from './use-reset-on-return';

/**
 * Sends approved users (CVP `userFlow === "APPROVED"` / "APPROVED_NO_POPUP")
 * to the bank's card-management surface via a plain GET redirect. Call
 * `openManageCard` on the bottom-CTA click for that flow.
 */
export function useManageCard() {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<unknown>(null);

	const isMounted = useRef(true);
	useEffect(() => {
		// Re-arm on every effect run — StrictMode runs mount → cleanup → mount,
		// so without this the ref stays false and state updates get dropped.
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	// `isLoading` intentionally stays true through the bank navigation; release it
	// when the user comes Back (the handoff is a cross-origin
	// window.location.assign, so this is a hard reload, not a bfcache restore —
	// see useResetOnReturn).
	useResetOnReturn(() => setIsLoading(false));

	const openManageCard =
		useCallback(async (): Promise<BankRedirectionResponse | null> => {
			setIsLoading(true);
			setError(null);
			try {
				const response = await fetchManageCard();
				const redirected = handleManageCardRedirection(response);
				// Fired only when the manage-card redirect actually fires (redirect
				// URL returned), just before the page navigates away.
				if (redirected) trackCbccEvent(CBCC_EVENT.MANAGE_CARD);
				if (!redirected && isMounted.current) setIsLoading(false);
				return response;
			} catch (err) {
				showCbccErrorToast();
				if (isMounted.current) {
					setError(err);
					setIsLoading(false);
				}
				return null;
			}
		}, []);

	return { openManageCard, isLoading, error };
}
